import type { CheckedState } from '@radix-ui/react-checkbox';
import * as RadixSelect from '@radix-ui/react-select';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Icon } from '~/components/granular';
import { contextFactory } from '~/utils/contextFactory';
import { classNames } from '~/utils/styles';
import type { DropdownContextProps, DropdownObjectItem, DropdownProps } from './';
import { DropdownActions, DropdownEmptyState, DropdownTrigger } from './';

export const getLabel = (item: DropdownObjectItem | string) => {
  return typeof item === 'string' ? item : item.label;
};

export const getValue = (item: DropdownObjectItem | string) => {
  return typeof item === 'string' ? item : item.value;
};

const contentClasses = classNames(
  // Marks the portalled menu as part of any AG Grid popup it opens from (e.g. a date filter's calendar),
  // so picking an item does not close that popup. It has no styles.
  'ag-custom-component-popup',
  'z-20 max-h-[450px] w-full bg-white px-0 shadow-md',
  'relative top-[-2px] flex flex-col items-start',
  'border border-neutral-200',
);

export const [DropdownContext, useDropdownContext] = contextFactory<DropdownContextProps>();

export const Dropdown = forwardRef<HTMLDivElement, DropdownProps>(
  (
    {
      applyBtnText = 'Apply',
      autoFocusTrigger,
      children,
      className,
      clearBtnText = 'Clear all',
      clearable = false,
      clearLabel = 'Clear',
      closeOnSelect = true,
      customTrigger,
      disabled,
      disabledItemTooltipText,
      dropdownOffset = 3,
      dropdownTriggerWidth,
      fixedLabel,
      groupBy,
      hasError,
      hideApplyButton = false,
      hideClearButton = false,
      id,
      incompatibleItems = [],
      indeterminateItems = [],
      isMultiple = false,
      isOpen = false,
      items = [],
      limit,
      maxDisplayedItems,
      noDataText = 'No results found',
      onClear,
      onEscapeKeydown,
      onOpenChange,
      onPointerDownOutside,
      onValueChange,
      placeholder,
      position = 'popper',
      selectAllText = () => 'Select all',
      selectedItems = [],
      shortcutItems = [],
      triggerProps = {},
      triggerRef,
      unselectAllText = () => 'Unselect all',
    }: DropdownProps,
    forwardedRef,
  ) => {
    const internalTriggerRef = useRef<HTMLElement | null>(null);
    useImperativeHandle(triggerRef, () => internalTriggerRef.current as HTMLElement);
    // auto focus the trigger if autoFocusTrigger is true
    useEffect(() => {
      if (internalTriggerRef.current && autoFocusTrigger) {
        internalTriggerRef.current.focus();
      }
    }, [autoFocusTrigger]);

    const [openState, setOpenState] = useState(isOpen);
    const [searchValue, setSearchValue] = useState('');

    const [tempSelection, setTempSelection] = useState<string | string[]>(selectedItems);

    if (shortcutItems.length && !isMultiple) {
      console.error('Dropdown: Shortcut items are only available when isMultiple is true');
    }

    // used to restrict the number of selected items to the limit
    const [disableNonSelectedItems, setDisableNonSelectedItems] = useState(false);

    const disabledIncompatibleItems = useMemo(() => {
      if (!isMultiple || !incompatibleItems.length) {
        return [];
      }

      const currentSelection = Array.isArray(tempSelection) ? tempSelection : [tempSelection];
      return incompatibleItems.filter((group) => group.some((item) => currentSelection.includes(item))).flat();
    }, [tempSelection, incompatibleItems, isMultiple]);

    const handleOpenChange = (open: boolean) => {
      // Radix also reports a close on a window blur or resize, which leaves the list open (`openState` stays
      // true). The owner hears a close only from the handlers below that really close the list.
      if (!open) {
        return;
      }

      // The staged selection only diverges from the prop while the user edits inside an open dropdown —
      // so opening re-reads the prop, picking up any selection the owner changed while it was closed.
      setTempSelection(selectedItems);

      if (limit) {
        setDisableNonSelectedItems(selectedItems.length >= limit);
      }

      setOpenState(true);
      onOpenChange?.(true);
    };

    const [measuredTriggerWidth, setMeasuredTriggerWidth] = useState(0);
    useLayoutEffect(() => {
      if (openState && internalTriggerRef.current) {
        setMeasuredTriggerWidth(internalTriggerRef.current.getBoundingClientRect().width);
      }
    }, [openState]);
    const triggerWidth = dropdownTriggerWidth || measuredTriggerWidth;

    function closeList() {
      setOpenState(false);
      onOpenChange?.(false);
      setSearchValue('');
    }

    function closeDropdown() {
      setTempSelection(isMultiple && hideApplyButton ? tempSelection : selectedItems);
      closeList();
    }

    function handleApplyButtonClick() {
      onValueChange?.(tempSelection);
      closeList();
    }

    function handleClearAllButtonClick() {
      setTempSelection([]);
      setDisableNonSelectedItems(false);
    }

    function handleEscapeKeydown(e: globalThis.KeyboardEvent) {
      onEscapeKeydown?.(e);
      closeDropdown();
    }

    function handlePointerDownOutside(e: CustomEvent<{ originalEvent: PointerEvent }>) {
      onPointerDownOutside?.(e);
      closeDropdown();
    }

    function handleSearchChange(e: React.ChangeEvent<HTMLInputElement>) {
      setSearchValue(e.target.value);
    }

    function handleToggleSelectAll() {
      const values = items.map((item: DropdownObjectItem | string) => getValue(item));
      const nextSelection = areAllItemsSelected() ? [] : values;

      if (limit) {
        setDisableNonSelectedItems(nextSelection.length >= limit);
      }

      setTempSelection(nextSelection);
    }

    const isItemSelected = useCallback(
      (item: DropdownObjectItem | string): CheckedState => {
        let isSelected = false;

        if (isMultiple) {
          isSelected = (tempSelection as string[]).includes(getValue(item));

          // Check against shortcut items
          if (typeof item !== 'string' && item.isShortcut) {
            isSelected = item.value
              .split('/')
              .map((v) => v.trim())
              .every((v) => (tempSelection as string[]).includes(v));
          }
        } else {
          isSelected = selectedItems === getValue(item);
        }

        if (isSelected) {
          return isSelected;
        }

        const isIndeterminate = isMultiple ? indeterminateItems.includes(getValue(item)) : false;
        if (isIndeterminate) {
          return 'indeterminate';
        }

        return false;
      },
      [tempSelection, selectedItems, indeterminateItems, isMultiple],
    );

    function handleValueChange(newValues: string | string[]) {
      // when isMultiple is false, newValues is always a single string
      if (!isMultiple) {
        onValueChange?.(newValues);
        if (closeOnSelect) {
          closeList();
        }
        return;
      }

      // if has values unchecked, check all, otherwise uncheck all
      // this works either if newValues is from a group or single element selection
      const hasValuesUnchecked = (newValues as string[]).some((v) => !tempSelection.includes(v));

      const finalArray = hasValuesUnchecked
        ? [...new Set([...tempSelection, ...newValues])]
        : [...new Set([...tempSelection].filter((value) => value !== '' && !newValues.includes(value)))];

      if (limit) {
        setDisableNonSelectedItems(finalArray.length >= limit);
      }

      setTempSelection(finalArray);
      if (hideApplyButton) {
        onValueChange?.(finalArray);
      }
    }

    const isItemHidden = useMemo(
      () =>
        (item: DropdownObjectItem | string): boolean => {
          if (typeof item === 'string') {
            return false;
          }
          return !!item.hidden;
        },
      [],
    );

    const getDisplayValue = useCallback(() => {
      if (Array.isArray(tempSelection)) {
        return (tempSelection as string[]).join(', ');
      }
      return selectedItems as string;
    }, [tempSelection, selectedItems]);

    const getDisplayLabelByValue = useCallback(
      (value = isMultiple ? tempSelection : selectedItems) => {
        if (isMultiple && Array.isArray(tempSelection)) {
          if (!value || !value.length) {
            return '';
          }
          const labels = (tempSelection as string[]).map((value) => {
            const dropdownItem = items.find((i: DropdownObjectItem | string) => getValue(i) === value) || '';
            return getLabel(dropdownItem);
          });

          if (maxDisplayedItems !== undefined && labels.length > maxDisplayedItems) {
            return `${labels.slice(0, maxDisplayedItems).join(', ')} +${labels.length - maxDisplayedItems}`;
          }

          return labels.join(', ');
        }

        if (!value) {
          return '';
        }

        const item = items.find((item: DropdownObjectItem | string) => getValue(item) === value) || '';
        return getLabel(item);
      },
      [tempSelection, isMultiple, items, maxDisplayedItems, selectedItems],
    );

    const areAllItemsSelected = useCallback((): CheckedState => {
      if (!isMultiple) {
        return false;
      }
      return (tempSelection as string[]).length === items.length;
    }, [tempSelection, isMultiple, items]);

    const filteredItems = useMemo(() => {
      return items.filter((item: DropdownObjectItem | string) => {
        if (isItemHidden(item)) {
          return false;
        }

        // Keep the selected item mounted while searching: Radix refocuses the listbox when the
        // selected item unmounts, which steals focus from the search input mid-typing
        if (!isMultiple && getValue(item) === selectedItems) {
          return true;
        }

        return getLabel(item).toLowerCase().indexOf(searchValue.toLowerCase()) !== -1;
      });
    }, [searchValue, items, isItemHidden, isMultiple, selectedItems]);

    const selectAllTextOrFn = areAllItemsSelected() ? unselectAllText : selectAllText;
    const selectAllLabel =
      typeof selectAllTextOrFn === 'function' ? selectAllTextOrFn(items.length) : selectAllTextOrFn;

    const showClearButton = clearable && !disabled && selectedItems.length > 0;

    return (
      // The clear button sits beside the trigger rather than in it: the trigger is a button, which can't hold one.
      <div className={classNames('relative', { '[&_input]:pr-12!': showClearButton }, className)}>
        <RadixSelect.Root
          disabled={disabled}
          onOpenChange={handleOpenChange}
          open={openState}
          value={getDisplayValue()}
        >
          <DropdownTrigger
            customTrigger={customTrigger}
            fixedLabel={fixedLabel}
            hasError={hasError}
            id={id}
            isDisabled={disabled}
            isOpen={openState}
            placeholder={placeholder}
            ref={internalTriggerRef}
            value={getDisplayLabelByValue()}
            {...triggerProps}
          />
          <RadixSelect.Portal>
            <DropdownContext.Provider
              value={{
                areAllItemsSelected,
                closeDropdown,
                disabledIncompatibleItems,
                disabledItemTooltipText,
                disableNonSelectedItems,
                getLabel,
                getValue,
                groupBy,
                handleSearchChange,
                handleToggleSelectAll,
                handleValueChange,
                isItemSelected,
                isMultiple,
                items: filteredItems,
                searchValue,
                selectAllLabel,
                shortcutItems,
              }}
            >
              <RadixSelect.Content
                alignOffset={-1}
                asChild
                className={contentClasses}
                onEscapeKeyDown={handleEscapeKeydown}
                onPointerDownOutside={handlePointerDownOutside}
                position={position}
                ref={forwardedRef}
                sideOffset={dropdownOffset}
              >
                <div>
                  <RadixSelect.Viewport style={{ scrollbarWidth: 'thin', width: triggerWidth }}>
                    {children}
                  </RadixSelect.Viewport>
                  {noDataText !== null && <DropdownEmptyState text={noDataText} />}
                  {isMultiple && (
                    <DropdownActions
                      applyBtnText={applyBtnText}
                      clearBtnText={clearBtnText}
                      handleApplyButtonClick={handleApplyButtonClick}
                      handleClearAllButtonClick={handleClearAllButtonClick}
                      hideApplyButton={hideApplyButton}
                      hideClearButton={hideClearButton}
                    />
                  )}
                </div>
              </RadixSelect.Content>
            </DropdownContext.Provider>
          </RadixSelect.Portal>
        </RadixSelect.Root>

        {showClearButton && (
          <button
            aria-label={clearLabel}
            className="absolute top-1/2 right-8 flex -translate-y-1/2 cursor-pointer text-neutral-600 hover:text-teal-800"
            onClick={onClear}
            type="button"
          >
            <Icon height={16} icon="CloseIcon" role="img" width={16} />
          </button>
        )}
      </div>
    );
  },
);
